/**
 * Geometry and slot rules for the drawing tool wheel: a ring of rounded
 * slices around a round hub, opened at the pointer while Tab is held. Pure functions, so the
 * gesture can be tested without a browser.
 *
 * Selection is by DIRECTION from the hub, not by landing on a tile: every point
 * outside the hub belongs to the slot whose angle is nearest, however far the
 * pointer has travelled. That is what lets a quick flick pick a tool.
 */
import { WHEEL_CONTROLS } from '../components/toolWheelControls'
import { ALL_OVERLAYS } from './overlays'

/**
 * The round hub's diameter, which is also the cancel zone (px). 132 at first,
 * then 120, then 10% smaller again (Chris, 2026-10-08).
 */
export const WHEEL_HUB = 108
export interface WheelRing {
  inner: number
  outer: number
  /** Width of the gap between neighbouring slices, the same from hub to rim. */
  gap: number
  innerCorner: number
  outerCorner: number
}

/** Everything that sizes the ring, at scale 1 (px). */
export interface WheelLayout {
  /** The square footprint: the ring plus a little margin. */
  size: number
  ring: WheelRing
  /** Distance from the center to each slot's icon. */
  slotRadius: number
  /** The icon block on each slice. */
  cellW: number
  cellH: number
}

/**
 * The wheel's one layout: icons only (Chris, 2026-10-08, dropped the names;
 * a slice's name is its tooltip and shows in the hub when it is in focus).
 */
export const WHEEL_LAYOUT: WheelLayout = {
  size: 288,
  ring: { inner: 70, outer: 140, gap: 5, innerCorner: 6, outerCorner: 10 },
  slotRadius: 105,
  cellW: 50,
  cellH: 50,
}

/** Gap kept between the wheel and the viewport edge (px). */
export const WHEEL_VIEWPORT_MARGIN = 8
/**
 * The wheel's size on screen, applied as one scale over everything above
 * (ring, icons, text, hub), so the proportions never drift. 1.1 = the
 * reference design plus 10% (Chris, 2026-10-08).
 */
export const WHEEL_SCALE = 1.1

/**
 * Clockwise from 12 o clock. Keeps the reference design's positions wherever
 * the tool exists in our catalog, and carries all five default rail favorites
 * (measure and positionPlan sit where the design had cursor and brush).
 */
export const DEFAULT_TOOL_WHEEL_SLOTS: readonly string[] = [
  'straightLine',
  'horizontalStraightLine',
  'verticalStraightLine',
  'rayLine',
  'rect',
  'measure',
  'positionPlan',
  'horizontalRayLine',
  'fibonacciLine',
  'parallelStraightLine',
]

const CATALOG_NAMES = ALL_OVERLAYS.map((t) => t.name)
/** What a slot may hold: a drawing tool, or one of the bar's mode controls. */
const SLOT_NAMES = new Set([...CATALOG_NAMES, ...WHEEL_CONTROLS.map((c) => c.id)])

export interface WheelPoint {
  x: number
  y: number
}

/** Angle of slot `i` of `n`, in radians, with slot 0 at 12 o clock. */
export function slotAngle(i: number, n: number): number {
  return -Math.PI / 2 + (i * 2 * Math.PI) / n
}

/**
 * The slot a point selects, by its direction from `center`, or null while the
 * point is still inside the hub (the cancel zone). `scale` is the wheel's
 * viewport fit, which shrinks the hub along with everything else.
 */
export function slotFromPoint(
  point: WheelPoint,
  center: WheelPoint,
  scale: number,
  n: number,
): number | null {
  const dx = point.x - center.x
  const dy = point.y - center.y
  if (Math.hypot(dx, dy) < (WHEEL_HUB / 2) * scale) return null
  const step = (2 * Math.PI) / n
  // atan2 measures from 3 o clock; shift so 12 o clock is zero.
  const angle = Math.atan2(dy, dx) + Math.PI / 2
  return ((Math.round(angle / step) % n) + n) % n
}

/**
 * Where the wheel opens for a pointer at `point`: on the pointer, at
 * `WHEEL_SCALE`, when the whole wheel fits; slid inward near an edge; and
 * scaled down in a viewport smaller than the wheel itself.
 */
export function fitWheelToViewport(
  point: WheelPoint,
  viewport: { width: number; height: number },
  size: number = WHEEL_LAYOUT.size,
): WheelPoint & { scale: number } {
  const room = 2 * WHEEL_VIEWPORT_MARGIN
  const scale = Math.min(
    WHEEL_SCALE,
    (viewport.width - room) / size,
    (viewport.height - room) / size,
  )
  const half = (size * scale) / 2 + WHEEL_VIEWPORT_MARGIN
  const clamp = (v: number, max: number) =>
    max < half * 2 ? max / 2 : Math.min(Math.max(v, half), max - half)
  return { x: clamp(point.x, viewport.width), y: clamp(point.y, viewport.height), scale }
}

/**
 * Put `tool` into `slot`. A tool already elsewhere in the wheel trades places
 * with the slot's current tool, so the wheel never holds a tool twice.
 */
export function swapSlot(slots: readonly string[], slot: number, tool: string): string[] {
  const next = [...slots]
  const existing = next.indexOf(tool)
  if (existing !== -1 && existing !== slot) next[existing] = next[slot]
  next[slot] = tool
  return next
}

/**
 * A saved layout made safe to render: unknown and repeated names dropped,
 * truncated to the slot count, and padded back out from the defaults (then the
 * drawing catalog). Controls a user placed are kept. A layout is repaired, never thrown away, so a catalog change or a
 * different slot count costs the user nothing they chose.
 */
export function sanitizeWheelSlots(
  saved: unknown,
  defaults: readonly string[] = DEFAULT_TOOL_WHEEL_SLOTS,
): string[] {
  const n = defaults.length
  const out: string[] = []
  if (Array.isArray(saved)) {
    for (const name of saved) {
      if (out.length === n) break
      if (typeof name === 'string' && SLOT_NAMES.has(name) && !out.includes(name)) out.push(name)
    }
  }
  const fill = [...defaults, ...CATALOG_NAMES]
  for (const name of fill) {
    if (out.length === n) break
    if (SLOT_NAMES.has(name) && !out.includes(name)) out.push(name)
  }
  return out
}

type Vec = [number, number]

export interface RingSlice {
  /** The slice outline, rounded at all four corners (SVG / CSS `path()` data). */
  d: string
  /**
   * The eight tangent points, clockwise from the outer-left corner's end on
   * the rim: outer-left, outer-right (rim), outer-right, inner-right (right
   * edge), inner-right, inner-left (inner arc), inner-left, outer-left (left
   * edge). Absolute coordinates.
   */
  points: Vec[]
}

/**
 * Slice `i` of `n` around `center`, as one rounded piece of the ring. Its two
 * sides run parallel to the dividing radii, offset by half the gap, so every
 * gap is the same width end to end; the corners are true fillets between each
 * side and the ring's arcs. Every slice is the same shape turned to its slot.
 */
export function ringSlice(
  i: number,
  n: number,
  center: number,
  ring: WheelRing = WHEEL_LAYOUT.ring,
): RingSlice {
  const half = Math.PI / n
  const mid = slotAngle(i, n)
  const off = ring.gap / 2
  const dir = (a: number): Vec => [Math.cos(a), Math.sin(a)]
  // Tangent points of the fillet (radius k) between a side and a ring arc
  // (radius rho). `normal` points from the dividing radius into the slice.
  const fillet = (divider: number, normal: Vec, rho: number, k: number, outer: boolean) => {
    const dist = outer ? rho - k : rho + k
    const t = Math.sqrt(dist * dist - (off + k) * (off + k))
    const u = dir(divider)
    const fx = (off + k) * normal[0] + t * u[0]
    const fy = (off + k) * normal[1] + t * u[1]
    const onSide: Vec = [center + off * normal[0] + t * u[0], center + off * normal[1] + t * u[1]]
    const onArc: Vec = [center + (fx * rho) / dist, center + (fy * rho) / dist]
    return { onSide, onArc }
  }
  const left = mid - half
  const right = mid + half
  const intoFromLeft = dir(left + Math.PI / 2)
  const intoFromRight = dir(right - Math.PI / 2)
  const ol = fillet(left, intoFromLeft, ring.outer, ring.outerCorner, true)
  const or = fillet(right, intoFromRight, ring.outer, ring.outerCorner, true)
  const ir = fillet(right, intoFromRight, ring.inner, ring.innerCorner, false)
  const il = fillet(left, intoFromLeft, ring.inner, ring.innerCorner, false)
  const f = (v: Vec) => `${v[0].toFixed(2)} ${v[1].toFixed(2)}`
  const R = ring.outer
  const r = ring.inner
  const ko = ring.outerCorner
  const ki = ring.innerCorner
  const d = [
    `M${f(ol.onArc)}`,
    `A${R} ${R} 0 0 1 ${f(or.onArc)}`,
    `A${ko} ${ko} 0 0 1 ${f(or.onSide)}`,
    `L${f(ir.onSide)}`,
    `A${ki} ${ki} 0 0 1 ${f(ir.onArc)}`,
    `A${r} ${r} 0 0 0 ${f(il.onArc)}`,
    `A${ki} ${ki} 0 0 1 ${f(il.onSide)}`,
    `L${f(ol.onSide)}`,
    `A${ko} ${ko} 0 0 1 ${f(ol.onArc)}`,
    'Z',
  ].join(' ')
  return {
    d,
    points: [ol.onArc, or.onArc, or.onSide, ir.onSide, ir.onArc, il.onArc, il.onSide, ol.onSide],
  }
}
